/**
 * Trust Center — public /trust page.
 *
 * Live security posture snapshot + SOC 2-lite report download.
 * Slate/blue palette to differentiate from AI Act (indigo/fuchsia) and NIS2 (emerald/cyan).
 * Doubles as a lead magnet — email required to download the signed report.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck, Lock, KeyRound, Server, Activity, FileText, Mail, Building2, User as UserIcon,
  CheckCircle2, AlertTriangle, Loader2, ArrowRight, Sparkles, ExternalLink, Copy, Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import useMetaTags from '../../hooks/useMetaTags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONTROL_ICONS = {
  ok: <CheckCircle2 className="h-4 w-4 text-emerald-400" />,
  degraded: <AlertTriangle className="h-4 w-4 text-amber-400" />,
  fail: <AlertTriangle className="h-4 w-4 text-red-400" />,
};

export default function TrustCenter() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', company: '', role: '' });
  const [submitting, setSubmitting] = useState(false);
  const [downloadInfo, setDownloadInfo] = useState(null);

  useEffect(() => { document.title = 'NIS2AI · Trust Center'; }, []);
  useMetaTags({
    title: 'NIS2AI · Trust Center — Segurança em tempo real',
    description: 'Estado ao vivo dos controlos de segurança da plataforma NIS2AI: encriptação, sub-processadores, âncora de integridade SHA-256. Descarrega um Trust Report assinado.',
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/trust/status`);
        if (mounted) setStatus(data);
      } catch (err) {
        if (mounted) toast.error('Não foi possível carregar o estado da plataforma.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const canSubmit = form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email) && form.company.trim().length >= 1;

  const requestReport = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/public/trust/report`, form);
      setDownloadInfo(data);
      // Kick off the download automatically
      window.location.href = `${BACKEND_URL}/api/public/trust/report/${data.download_token}`;
      toast.success('Relatório emitido. O download vai iniciar automaticamente.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha a emitir o relatório.');
    } finally {
      setSubmitting(false);
    }
  };

  const copyHash = () => {
    if (!status?.integrity?.audit_chain_hash) return;
    navigator.clipboard.writeText(status.integrity.audit_chain_hash);
    toast.success('Hash copiado.');
  };

  const overallOk = status?.overall_status === 'operational';

  return (
    <div className="min-h-screen bg-[#050a15] text-slate-100 relative overflow-hidden">
      {/* Ambient — slate/blue distinct from AI Act/NIS2 */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute top-[-15%] left-1/3 h-[520px] w-[520px] rounded-full bg-sky-500/15 blur-[130px]" />
        <div className="absolute bottom-[-10%] right-[-5%] h-[420px] w-[420px] rounded-full bg-blue-600/20 blur-[130px]" />
        <div className="absolute top-1/3 left-[-10%] h-[380px] w-[380px] rounded-full bg-slate-500/10 blur-[100px]" />
      </div>
      <div className="pointer-events-none absolute inset-0 [background:linear-gradient(rgba(148,163,184,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.04)_1px,transparent_1px)] [background-size:48px_48px]" />

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5 border-b border-white/5">
        <Link to="/trust" className="flex items-center gap-2 font-semibold tracking-tight" data-testid="trust-logo">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <span className="text-lg">NIS2<span className="text-sky-400">AI</span> · Trust</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/landing/ai-act" className="text-sm text-slate-400 hover:text-white transition-colors hidden md:inline" data-testid="trust-crosslink-aiact">
            AI Act →
          </Link>
          <Link to="/landing/nis2" className="text-sm text-slate-400 hover:text-white transition-colors hidden md:inline" data-testid="trust-crosslink-nis2">
            NIS2 →
          </Link>
          <Link to="/login" className="text-sm text-slate-400 hover:text-white transition-colors" data-testid="trust-login-link">Entrar</Link>
        </div>
      </nav>

      <main className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-16" data-testid="trust-hero">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-xs text-slate-300 mb-6">
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 text-sky-400 animate-spin" />
            ) : overallOk ? (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
            ) : (
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
            )}
            <span data-testid="trust-overall-status">
              {loading ? 'A verificar...' : overallOk ? 'Todos os sistemas operacionais' : 'Estado degradado'}
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Transparência em
            <span className="block bg-gradient-to-r from-sky-400 via-blue-400 to-slate-300 bg-clip-text text-transparent"> tempo real.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed">
            Estado ao vivo dos controlos de segurança que sustentam a plataforma NIS2AI. Descarrega um <b className="text-slate-200">relatório assinado SHA-256</b> em segundos.
          </p>
        </div>

        {/* Live status grid */}
        {loading ? (
          <SkeletonGrid />
        ) : status ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16" data-testid="trust-status-grid">
            <StatusCard
              icon={KeyRound}
              title="Encriptação at-rest"
              value={status.encryption.at_rest.active ? 'Ativa' : 'Inativa'}
              ok={status.encryption.at_rest.active}
              hint={status.encryption.at_rest.algorithm}
              testid="status-encryption-atrest"
            />
            <StatusCard
              icon={Lock}
              title="Encriptação in-transit"
              value={status.encryption.in_transit.protocol}
              ok={status.encryption.in_transit.active}
              hint="Obrigatório em todos os endpoints"
              testid="status-encryption-intransit"
            />
            <StatusCard
              icon={Activity}
              title="Monitorização contínua"
              value={`${status.scheduler.count} jobs ativos`}
              ok={status.scheduler.count > 0}
              hint="SLA incidentes · retry queue · alertas"
              testid="status-scheduler"
            />
          </div>
        ) : null}

        {/* Controls */}
        {status && (
          <section className="mb-16" data-testid="trust-controls-section">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2">Controlos de segurança</h2>
            <p className="text-sm text-slate-400 mb-6">Cada controlo é avaliado em tempo real e refletido no relatório PDF.</p>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/[0.03] border-b border-white/5">
                  <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="px-4 py-3 w-16">Cód.</th>
                    <th className="px-4 py-3">Controlo</th>
                    <th className="px-4 py-3 w-24">Estado</th>
                    <th className="px-4 py-3">Detalhe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {status.controls.map(c => (
                    <tr key={c.code} data-testid={`control-${c.code}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{c.code}</td>
                      <td className="px-4 py-3 font-medium text-slate-100">{c.name}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                          {CONTROL_ICONS[c.status] || CONTROL_ICONS.ok}
                          {c.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-400 leading-relaxed">{c.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Integrity anchor */}
        {status?.integrity?.audit_chain_hash && (
          <section className="mb-16 rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-md" data-testid="trust-integrity">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-sky-400 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-100">Âncora de integridade</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Hash SHA-256 dos últimos 100 registos do audit trail em ordem canónica. Qualquer alteração retroativa invalida o hash.
                </p>
                <div className="mt-3 flex items-center gap-2 overflow-hidden">
                  <code className="font-mono text-[11px] text-slate-300 bg-slate-900/60 px-3 py-1.5 rounded-lg border border-white/5 truncate" data-testid="integrity-hash">
                    {status.integrity.audit_chain_hash}
                  </code>
                  <Button size="sm" variant="ghost" onClick={copyHash} className="text-slate-400 hover:text-white" data-testid="copy-hash-btn">
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Sub-processors */}
        {status && (
          <section className="mb-16" data-testid="trust-subprocessors">
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2">Sub-processadores</h2>
            <p className="text-sm text-slate-400 mb-6">Terceiros que processam dados em nosso nome, com DPA e certificações verificadas.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {status.subprocessors.map((s, i) => (
                <div key={i} className="p-5 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-100 flex items-center gap-2"><Server className="h-4 w-4 text-sky-400" />{s.name}</p>
                      <p className="text-xs text-slate-400 mt-1.5">{s.purpose}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] uppercase tracking-widest text-slate-500">Região</p>
                      <p className="text-xs text-slate-300 font-medium">{s.region}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-white/5 flex items-center gap-2">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                    <p className="text-[11px] text-slate-400">{s.cert}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Report request */}
        <section className="rounded-3xl border border-white/10 bg-gradient-to-br from-sky-500/10 via-blue-500/10 to-slate-500/5 p-8 md:p-12 backdrop-blur-md" data-testid="trust-report-section">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sky-400/30 bg-sky-400/10 text-xs text-sky-300 mb-4">
                <Sparkles className="h-3 w-3" />
                PDF assinado SHA-256 · emitido em segundos
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight leading-snug">Descarregue o Trust Report</h2>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                Documento em PDF cobrindo criptografia, RBAC, audit trail, sub-processadores e âncora de integridade. Ideal para partilhar com equipas de compliance e legal.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> 9 controlos avaliados em tempo real</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Hash SHA-256 verificável no header do PDF</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Lista completa de sub-processadores</li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <div className="space-y-3 bg-slate-900/40 border border-white/10 rounded-2xl p-5" data-testid="trust-report-form">
                <div>
                  <Label htmlFor="tc-name" className="text-slate-300 text-xs uppercase tracking-widest">Nome</Label>
                  <div className="relative mt-1.5">
                    <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="tc-name"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="Maria Silva"
                      className="pl-9 bg-slate-950/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-sky-400"
                      data-testid="tc-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="tc-email" className="text-slate-300 text-xs uppercase tracking-widest">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="tc-email"
                      type="email"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="maria@empresa.pt"
                      className="pl-9 bg-slate-950/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-sky-400"
                      data-testid="tc-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="tc-company" className="text-slate-300 text-xs uppercase tracking-widest">Empresa</Label>
                  <div className="relative mt-1.5">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="tc-company"
                      value={form.company}
                      onChange={e => setForm({ ...form, company: e.target.value })}
                      placeholder="Acme Lda"
                      className="pl-9 bg-slate-950/60 border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-sky-400"
                      data-testid="tc-company-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={requestReport}
                  disabled={!canSubmit || submitting}
                  className="w-full bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white rounded-full font-semibold h-11 disabled:opacity-40"
                  data-testid="tc-submit-btn"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A gerar…</> : <><Download className="h-4 w-4 mr-2" />Descarregar Trust Report</>}
                </Button>
                {downloadInfo && (
                  <p className="text-[11px] text-slate-400 text-center pt-1" data-testid="tc-download-info">
                    Token válido até {new Date(downloadInfo.expires_at).toLocaleTimeString('pt-PT')}. <br />
                    Se o download não iniciou, <a href={`${BACKEND_URL}/api/public/trust/report/${downloadInfo.download_token}`} className="text-sky-400 underline" data-testid="tc-manual-download-link">clique aqui</a>.
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="mt-16 text-center">
          <p className="text-sm text-slate-400">
            Para DPA personalizado, testes de penetração ou auditoria de terceiros, contacte{' '}
            <a href="mailto:contacto@nis2ai.pt" className="text-sky-400 hover:text-sky-300 inline-flex items-center gap-1" data-testid="trust-contact-email">
              contacto@nis2ai.pt <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/5 py-8 px-6 text-center text-xs text-slate-500">
        <p>
          © {new Date().getFullYear()} NIS2AI · Trust Center ·
          {' '}<Link to="/landing/ai-act" className="hover:text-white underline underline-offset-2">EU AI Act</Link>
          {' · '}
          <Link to="/landing/nis2" className="hover:text-white underline underline-offset-2">NIS2</Link>
        </p>
      </footer>
    </div>
  );
}

function StatusCard({ icon: Icon, title, value, ok, hint, testid }) {
  return (
    <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-md" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{title}</p>
          <p className="text-xl font-semibold text-slate-100 mt-1">{value}</p>
          {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
        </div>
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${ok ? 'bg-emerald-500/15' : 'bg-amber-500/15'}`}>
          <Icon className={`h-5 w-5 ${ok ? 'text-emerald-400' : 'text-amber-400'}`} />
        </div>
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
      {[0, 1, 2].map(i => (
        <div key={i} className="p-5 rounded-2xl border border-white/10 bg-white/[0.02] animate-pulse">
          <div className="h-3 w-24 bg-white/5 rounded" />
          <div className="h-6 w-32 bg-white/10 rounded mt-3" />
          <div className="h-2 w-40 bg-white/5 rounded mt-2" />
        </div>
      ))}
    </div>
  );
}
